import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { QueryEntity } from '../types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/**
 * Toggle matter-level permission overrides. Matter creators can call this on
 * matters they created without holding UserManage; enabling for the first time
 * grants the creator the Administrator role within the matter.
 */
export const useSetMatterPermissionOverrides = (matterId: string | undefined) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const setOverrides = async (enabled: boolean) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.PATCH,
      data: { enabled },
    });

    const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/permission-overrides`, fetchConfig);
    if (!response.ok) {
      let errorMessage = 'Error updating Matter permission overrides';
      try {
        const data = await response.json();
        errorMessage = data.message || errorMessage;
      } catch {
        // Response is not JSON, use default message
      }
      throw new Error(errorMessage);
    }
  };

  return useMutation({
    mutationFn: setOverrides,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryEntity.Matter, { matterId }] });
      queryClient.invalidateQueries({ queryKey: [QueryEntity.PermissionOverrides, { matterId }] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update Matter permissions. Please try again.');
    },
  });
};
